import { useState } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../context/AuthContext';
import { FRONTEND_URL } from '../config/env';

export interface RegisterData {
  email: string;
  password: string;
  name: string;
  client_type: 'small' | 'medium' | 'large' | 'distributor';
}

export const useRegister = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { login } = useAuth();

  const register = async (data: RegisterData) => {
    setLoading(true);
    setError(null);

    try {
      // 1. Registrar usuario en Supabase Auth
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email: data.email.trim(),
        password: data.password,
        options: {
          data: {
            name: data.name.trim(),
            client_type: data.client_type,
          },
          emailRedirectTo: `${FRONTEND_URL || window.location.origin}/auth/callback`
        }
      });

      if (authError) throw authError;

      if (!authData.user) {
        throw new Error('No se pudo crear la cuenta de usuario');
      }

      // 2. Intentar crear/actualizar fila en tabla profiles
      try {
        await supabase
          .from('profiles')
          .upsert([
            {
              id: authData.user.id,
              name: data.name.trim(),
              client_type: data.client_type,
            }
          ]);
      } catch (profileErr) {
        console.warn('Profile row insert notice:', profileErr);
      }

      // 3. Si Supabase requiere confirmación de email (sin sesión inmediata)
      if (authData.user && !authData.session) {
        return { 
          success: true, 
          needsConfirmation: true,
          message: '¡Cuenta creada! Revisa tu correo electrónico para confirmar tu cuenta y luego podrás ingresar.' 
        };
      }

      // 4. Si la sesión se inició automáticamente
      if (authData.session) {
        await login(authData.session.access_token, authData.user);
      }

      return { 
        success: true, 
        needsConfirmation: false,
        user: authData.user 
      };
    } catch (err: any) {
      console.error('Registration error:', err);
      const msg = err.message || 'Error en el registro';
      setError(msg);
      return { success: false, error: msg };
    } finally {
      setLoading(false);
    }
  };

  return { register, loading, error };
};
